import { t } from '@lingui/core/macro';
import { useId } from 'react';

import SpokeIcon from '@/assets/svg/loading.svg?react';
import { cn } from '@/lib/utils';

export type SpinnerProps = React.ComponentPropsWithoutRef<'svg'> & {
  variant?: 'spoke' | 'circle';
  size?: 'xs' | 'sm' | 'md' | 'lg';
};

const sizeClasses = {
  xs: 'size-4',
  sm: 'size-5',
  md: 'size-6',
  lg: 'size-13',
} as const;

const Spinner = ({ variant = 'circle', size = 'md', className, ...props }: SpinnerProps) => {
  const gradientId = useId();
  const animation = variant === 'spoke' ? 'animate-spin-steps' : 'animate-spin';
  const sharedProps = {
    role: 'status' as const,
    'aria-label': t`Loading...`,
    className: cn(sizeClasses[size], animation, 'text-green-600', className),
    ...props,
  };

  if (variant === 'spoke') {
    return <SpokeIcon {...sharedProps} />;
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      fill="none"
      viewBox="0 0 24 24"
      {...sharedProps}
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(182,223,89,0.3)" />
          <stop offset="100%" stopColor="#55682a" />
        </linearGradient>
      </defs>
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke={`url(#${gradientId})`}
        strokeWidth="2"
        fill="none"
        strokeDasharray="62.83 62.83"
        strokeDashoffset="0"
        strokeLinecap="round"
      />
    </svg>
  );
};

export default Spinner;
